import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { formatValue, formatDateTick } from './format';

// Track Tailwind's class-based dark mode so chart strokes stay legible on
// both themes (Recharts can't read `dark:` utility classes).
const useIsDark = () => {
  const read = () =>
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(read()));
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

// Chart language: two hue identities only. Gold = you (measured history +
// dotted projection); green = the healthier direction (Next + Ultimate).
const GREEN_DARK = '#10B981';
const GREEN_LIGHT = '#059669';

// Merge history + projection into one date-keyed series. The two lines share
// the "now" date so the dotted projection visually continues the solid line.
const buildSeries = (history, projection) => {
  const byDate = new Map();
  history.forEach((h) => byDate.set(h.date, { date: h.date, hist: h.value }));
  projection.forEach((p) => {
    const row = byDate.get(p.date) || { date: p.date };
    row.proj = p.value;
    byDate.set(p.date, row);
  });
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
};

// Lifts the hovered/touched point out to the parent (which renders the readout
// strip above the chart) and draws nothing itself — so the tooltip never covers
// the plot. Driven by the Tooltip's active lifecycle, which DOES fire on touch
// (unlike the chart's onMouseMove).
const ActiveReporter = ({ active, payload, report }) => {
  useEffect(() => {
    report(active && payload && payload.length ? payload[0].payload : null);
  });
  return null;
};

const TrajectoryChart = ({ metric }) => {
  const isDark = useIsDark();
  const GREEN = isDark ? GREEN_DARK : GREEN_LIGHT;    // Next + Ultimate
  const goldA = isDark ? '#FFB800' : '#B87D00';       // "you" line gradient stops
  const goldB = isDark ? '#FFE24A' : '#E09E00';
  const grid = isDark ? '#241E18' : '#e5e7eb';
  const axis = isDark ? '#8A7D6C' : '#6b7280';

  const data = useMemo(
    () => buildSeries(metric.history || [], metric.projection || []),
    [metric.history, metric.projection]
  );

  // The hovered/scrubbed point, surfaced in a fixed strip ABOVE the plot so the
  // readout never covers the chart (the old floating tooltip sat under the
  // finger on a short mobile chart). Defaults to the latest measured point.
  const [active, setActive] = useState(null);
  const shown = active; // hover/scrub only — no confusing idle date parked on the card
  const activeKey = useRef(null);
  // Guard so we only setState when the touched point actually changes (the
  // reporter's effect runs every render).
  const report = useCallback((point) => {
    const key = point ? point.date : null;
    if (key !== activeKey.current) {
      activeKey.current = key;
      setActive(point);
    }
  }, []);

  const t = metric.target || {};
  const hasUltimate = t.ultimate_low != null || t.ultimate_high != null;
  const hasNext =
    t.next_value != null && (t.next_low != null || t.next_high != null);

  // Y domain: cover data, both bands, and a little breathing room.
  const [yMin, yMax] = useMemo(() => {
    const vals = [];
    data.forEach((d) => {
      if (d.hist != null) vals.push(d.hist);
      if (d.proj != null) vals.push(d.proj);
    });
    [t.ultimate_low, t.ultimate_high, t.next_low, t.next_high].forEach(
      (v) => v != null && vals.push(v)
    );
    if (!vals.length) return [0, 1];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo || Math.abs(hi) || 1) * 0.08;
    // Every twin metric is non-negative, so never pad the axis below zero
    // (a "-1,117 steps" gridline reads as a bug even though it's just slack).
    return [Math.max(lo - pad, 0), hi + pad];
  }, [data, t.ultimate_low, t.ultimate_high, t.next_low, t.next_high]);

  return (
    <div>
      {/* Fixed readout strip — sits above the plot, never overlaps it. Shows the
          latest point by default; follows the finger/cursor while scrubbing. */}
      <div className="mb-1 min-h-[1.5rem] flex flex-wrap items-center gap-x-3 text-sm">
        {shown && (
          <>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatDateTick(shown.date)}
            </span>
            {shown.hist != null && (
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                {formatValue(shown.hist, metric.unit)}
              </span>
            )}
          </>
        )}
      </div>
      <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
        <defs>
          <linearGradient id="trajGold" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={goldA} />
            <stop offset="1" stopColor={goldB} />
          </linearGradient>
          <filter id="trajGlow" x="-20%" y="-60%" width="140%" height="220%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={formatDateTick}
          tick={{ fontSize: 14, fill: axis }}
          minTickGap={40}
          stroke={axis}
        />
        <YAxis
          domain={[yMin, yMax]}
          tick={{ fontSize: 14, fill: axis }}
          width={44}
          stroke={axis}
          tickFormatter={(v) =>
            metric.decimals ? v.toFixed(metric.decimals) : Math.round(v).toLocaleString()
          }
        />
        {/* Keeps the vertical cursor line + drives the active point via
            onMouseMove/onTouchMove; renders no floating box (readout is the
            strip above), so it can never cover the chart. */}
        <Tooltip
          cursor={{ stroke: axis, strokeOpacity: 0.5, strokeDasharray: '3 3' }}
          content={<ActiveReporter report={report} />}
        />

        {/* Ultimate band — faint green, where you're ultimately headed */}
        {hasUltimate && t.ultimate_low != null && t.ultimate_high != null && (
          <ReferenceArea
            y1={t.ultimate_low}
            y2={t.ultimate_high}
            fill={GREEN}
            fillOpacity={0.08}
            stroke={GREEN}
            strokeOpacity={0.22}
            ifOverflow="extendDomain"
          />
        )}
        {/* Next band — green, the reachable staircase step */}
        {hasNext && t.next_low != null && t.next_high != null && (
          <ReferenceArea
            y1={t.next_low}
            y2={t.next_high}
            fill={GREEN}
            fillOpacity={0.16}
            ifOverflow="extendDomain"
          />
        )}
        {hasNext && (
          <ReferenceLine
            y={metric.target.next_value}
            stroke={GREEN}
            strokeDasharray="4 3"
            strokeOpacity={0.75}
          />
        )}

        {/* Solid measured history — the glowing gold "you" line */}
        <Line
          type="monotone"
          dataKey="hist"
          stroke="url(#trajGold)"
          strokeWidth={2.75}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
          filter="url(#trajGlow)"
        />
        {/* Dotted "if nothing changes" projection — same gold, dimmed */}
        <Line
          type="monotone"
          dataKey="proj"
          stroke="url(#trajGold)"
          strokeWidth={2}
          strokeDasharray="5 4"
          strokeOpacity={0.72}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
      </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrajectoryChart;
